import React, { useEffect, useState } from 'react';
import { Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Icon } from '@shakkerai/components';
import { eventBus } from '@starflow/app/helpers';
import { translate } from '@shakkerai/util-i18n';
import iconZoomIn from './assets/zoom-in.svg';
import iconZoomOut from './assets/zoom-out.svg';
import iconMove from './assets/move.svg';
import iconMouse from './assets/mouse.svg';

const keyClassName =
  'flex items-center justify-center min-w-[20px] h-5 bg-gradient-to-b from-[#ffffff] to-[#f9fbfb] border border-[#ededed] rounded-sm text-[10px] font-semibold text-[#2d2d2d]';

export const Hotkeys = React.memo(() => {
  const [isMacOs, setTsMacOs] = useState(navigator.userAgent.includes('Mac OS X'));
  const [opened, { open, close }] = useDisclosure();
  const ctrlKeySymbol = isMacOs ? '⌘' : 'Ctrl';

  useEffect(() => {
    eventBus.on('event_request_open_hotkeys', open);

    return () => {
      eventBus.off('event_request_open_hotkeys', open);
    };
  }, [open]);

  return (
    <Popover opened={opened} position="bottom-center">
      <Popover.Target>
        <button
          className="flex items-center justify-center w-5 h-5 hover:bg-[#f2f4f6]"
          onMouseEnter={open}
          onMouseLeave={close}
        >
          <Icon className="shk-a-shortcutkey" fontSize={18} color="#7f7f7f" />
        </button>
      </Popover.Target>
      <Popover.Dropdown
        w={300}
        onMouseEnter={open}
        onMouseLeave={close}
        className="w-[300px] -mt-2 ml-0.5 p-0 pt-4 bg-transparent border-none"
      >
        <div className="bg-white rounded-sm shadow-light">
          <div className="flex items-center h-11 px-4 border-b border-[#efefef]">
            <span className="flex-1 text-[#2d2d2d] text-sm font-semibold">{translate('shortcut_title')}</span>
            <div className="flex p-0.5 bg-[#f2f4f6] rounded">
              <button
                disabled={isMacOs}
                onClick={() => setTsMacOs(true)}
                className="w-12 h-5 disabled:bg-white rounded-sm text-xs font-semibold text-[#7f7f7f]"
              >
                Mac
              </button>
              <button
                disabled={!isMacOs}
                onClick={() => setTsMacOs(false)}
                className="w-12 h-5 disabled:bg-white rounded-sm text-xs font-semibold text-[#7f7f7f]"
              >
                Win
              </button>
            </div>
          </div>
          <div className="flex flex-col p-4">
            <span className="text-[#2d2d2d] text-xs font-semibold">{translate('shortcut_zoom_title')}</span>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_keyboard')}</span>
              <span className={keyClassName + ' w-[34px]'}>{ctrlKeySymbol}</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName}>
                <Icon className="icon-prozhankai" fontSize={8} color="#2d2d2d" />
              </span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">/</span>
              <span className={keyClassName + ' w-[34px]'}>{ctrlKeySymbol}</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName}>
                <Icon className="icon-proshouqi" fontSize={8} color="#2d2d2d" />
              </span>
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_trackpad')}</span>
              <img src={iconZoomIn} className="w-6 h-6" alt="" />
              <span className="mx-1.5 text-xs text-[#8c8c8c]">/</span>
              <img src={iconZoomOut} className="w-6 h-6" alt="" />
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_mouse')}</span>
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_hold')}</span>
              <span className={keyClassName + ' w-[34px] mx-2'}>{ctrlKeySymbol}</span>
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_scrollmoues')}</span>
            </div>
            <span className="mt-3 text-[#2d2d2d] text-xs font-semibold">{translate('shortcut_pan_title')}</span>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_keyboard')}</span>
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_hold')}</span>
              <span className={keyClassName + ' w-[43px] mx-2'}>Space</span>
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_drag_2')}</span>
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_trackpad')}</span>
              <img src={iconMove} className="w-6 h-6" alt="" />
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_mouse')}</span>
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_hold')}</span>
              <img src={iconMouse} alt="" className="mx-2" />
              <span className="text-xs text-[#7f7f7f]">{translate('shortcut_drag_2')}</span>
            </div>
            <span className="mt-3 text-[#2d2d2d] text-xs font-semibold">{translate('shortcut_other_title')}</span>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_remove')}</span>
              <span className={keyClassName + ' w-[46px]'}>Delete</span>
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_undo')}</span>
              <span className={keyClassName + ' w-[34px]'}>{ctrlKeySymbol}</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName}>Z</span>
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_redo')}</span>
              <span className={keyClassName + ' w-[34px]'}>{ctrlKeySymbol}</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName + ' w-[37px]'}>Shift</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName}>Z</span>
            </div>
            <div className="flex items-center h-5 mt-2">
              <span className="flex-1 text-xs text-[#7f7f7f]">{translate('shortcut_locate')}</span>
              <span className={keyClassName + ' w-[34px]'}>{ctrlKeySymbol}</span>
              <span className="mx-1.5 text-xs text-[#8c8c8c]">+</span>
              <span className={keyClassName}>L</span>
            </div>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});
